// Sdílené primitivy info stránek (issue #38, screens/11-b2b.md, 13-servis.md,
// 15-reklamace.md, 16-odstoupeni.md + mobilní m11–m16).
//
// Info stránka = Shoptet `type-page`:
//   article.pageArticleDetail
//     > header[itemprop=headline] > h1     (nativní nadpis)
//     > div[itemprop=about]                (HTML z administrace — náš host)
//
// Obsah stránek žije v ADMINISTRACI, ne v repu. Bloky proto stavíme z reálného
// textu (odstavce, seznamy, odkazy), ne ze statických kopií — když klient text
// v adminu upraví, projeví se to i v designu. Co v obsahu není (procesní kroky,
// popisky u dlaždic), doplňujeme z návrhu jako KOPII, ne jako fakt o obchodu.
//
// Bez JS zůstane nativní článek čitelný — všechno, co nahrazujeme, schová CSS
// až pod `html.bs-info-ready`.

import { icon } from './icon.js';
import { phoneAction } from './headerTools.js';
import { asChatTrigger, chatActions } from './supportChat.js';
import { fillAvatar } from './teamPhoto.js';
import { bodyClass } from './pages.js';
import { currency } from './money.js';
import L from '../locale/cs/infoPage.js';

export const READY = 'bs-info-ready';

// --- mikro helpery ----------------------------------------------------------
export function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

export function link(href, cls, txt) {
  const node = el('a', cls, txt);
  if (href) node.href = href;
  return node;
}

export function textOf(node) {
  return node ? (node.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

// Text z administrace se zachovaným inline zvýrazněním. `textOf()` strhne
// i `<strong>`, které redaktor napsal záměrně (#20 z DR U6) — tohle projde
// uzly rekurzivně a přepíše je do cílového elementu jen v povoleném rozsahu
// značek. Nikdy nepoužívá innerHTML nad cizím obsahem, takže se sem nedá
// propašovat skript ani atribut z admin textu.
const RICH_TAGS = { STRONG: 'strong', B: 'strong', EM: 'em', I: 'em' };

export function fillRich(target, source) {
  if (!target) return target;
  if (!source) return target;
  const walk = (from, to) => {
    from.childNodes.forEach(function (n) {
      if (n.nodeType === 3) {
        const t = (n.nodeValue || '').replace(/\s+/g, ' ');
        if (t) to.appendChild(document.createTextNode(t));
        return;
      }
      if (n.nodeType !== 1) return;
      const tag = RICH_TAGS[n.tagName];
      if (tag) {
        const wrap = document.createElement(tag);
        walk(n, wrap);
        if (wrap.textContent.trim()) to.appendChild(wrap);
        return;
      }
      walk(n, to);
    });
  };
  walk(source, target);
  // krajní mezery z formátovaného admin markupu
  const first = target.firstChild;
  const last = target.lastChild;
  if (first && first.nodeType === 3) first.nodeValue = first.nodeValue.replace(/^\s+/, '');
  if (last && last.nodeType === 3) last.nodeValue = last.nodeValue.replace(/\s+$/, '');
  return target;
}

// Nezlomitelná mezera v číslech („1 200 Kč" se nesmí zlomit uprostřed ceny)
export function nbsp(value) {
  const sym = currency().symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return String(value)
    .replace(/(\d)\s+(?=\d)/g, '$1\u00A0')
    .replace(new RegExp('\\s+(' + sym + '|%)', 'g'), '\u00A0$1');
}

export function iconBox(name, cls) {
  const box = el('span', cls);
  box.appendChild(icon(name));
  return box;
}

// „Titulek + dovětek" nad sekcí (design: H2 24px + šedý dovětek 13px)
export function sectionHead(title, hint, cls) {
  const head = el('div', 'bs-ip__sechead' + (cls ? ' ' + cls : ''));
  head.appendChild(el('h2', 'bs-ip__h2', title));
  if (hint) head.appendChild(el('span', 'bs-ip__hint', hint));
  return head;
}

export function centerHead(title, perex) {
  const head = el('div', 'bs-ip__chead');
  head.appendChild(el('h2', 'bs-ip__h2 bs-ip__h2--center', title));
  if (perex) head.appendChild(el('p', 'bs-ip__cperex', perex));
  return head;
}

// --- hostitel stránky -------------------------------------------------------
// `[itemprop=about]` = blok z administrace. Vracíme i nativní H1, ať ho jednotky
// mohou PŘESUNOUT do hera (kopie by nechala v DOM dva H1).
export function pageHost() {
  const article = document.querySelector('article.pageArticleDetail');
  const about = document.querySelector('article.pageArticleDetail [itemprop="about"]');
  if (!about) return null;
  return {
    article: article,
    about: about,
    h1: article ? article.querySelector('header h1') : null,
  };
}

// Stránka podle klíče `pages` z manifestu shopu (`pageIs('contact')`): Shoptet
// dává info stránce `body.in-<slug>`, slug se liší mezi shopy. Stránka, kterou
// shop nemá, není nikdy aktivní.
export function pageIs(key) {
  const cls = bodyClass(key);
  return !!cls && document.body.classList.contains(cls);
}

export function markReady() {
  document.documentElement.classList.add(READY);
}

// --- hero -------------------------------------------------------------------
// def = { eyebrow, title (Node|string), perex (Node|string), note, ctas: [{href,label,icon,variant}] }
// `side` = pravý sloupec (fotka, stat karta) — když chybí, hero je jednosloupcové.
export function buildHero(def, side) {
  const hero = el('section', 'bs-ip__hero' + (side ? ' bs-ip__hero--split' : ''));
  const text = el('div', 'bs-ip__hero-text');

  if (def.eyebrow) text.appendChild(el('span', 'bs-ip__eyebrow', def.eyebrow));

  if (def.title) {
    // Nativní H1 se PŘESOUVÁ (SEO nadpis zůstává nadpisem stránky). Když sedí
    // v `header[itemprop=headline]`, stěhujeme celou hlavičku — jinak by po
    // přesunu zůstal v článku prázdný headline (schema.org).
    const h1 = typeof def.title === 'string' ? el('h1', null, def.title) : def.title;
    h1.classList.add('bs-ip__title');
    const header = h1.parentElement;
    if (header && header.tagName === 'HEADER') {
      header.classList.add('bs-ip__title-wrap');
      text.appendChild(header);
    } else {
      text.appendChild(h1);
    }
  }

  if (def.perex) {
    if (typeof def.perex === 'string') text.appendChild(el('p', 'bs-ip__perex', def.perex));
    else {
      def.perex.classList.add('bs-ip__perex');
      text.appendChild(def.perex);
    }
  }
  if (def.sub) {
    if (typeof def.sub === 'string') text.appendChild(el('p', 'bs-ip__sub', def.sub));
    else {
      def.sub.classList.add('bs-ip__sub');
      text.appendChild(def.sub);
    }
  }

  // CTA s `chat` / `chatless` / `chatVariant` řeší chatActions (supportChat.js)
  const ctas = def.ctas ? chatActions(def.ctas) : [];
  if (ctas.length) {
    const row = el('div', 'bs-ip__ctas');
    ctas.forEach(function (c) {
      const btn = link(c.href, 'bs-ip__cta' + (c.variant ? ' bs-ip__cta--' + c.variant : ''));
      if (c.icon) btn.appendChild(icon(c.icon));
      btn.appendChild(el('span', null, c.label));
      if (c.chat) asChatTrigger(btn, c.chat);
      row.appendChild(btn);
    });
    text.appendChild(row);
  }

  if (def.note) text.appendChild(el('div', 'bs-ip__note', def.note));

  hero.appendChild(text);
  if (side) hero.appendChild(side);
  return hero;
}

// Plovoucí karta nad fotkou / pod hero textem (štít „Záruka na opravy" apod.)
export function buildBadge(def) {
  const badge = el('div', 'bs-ip__badge' + (def.variant ? ' bs-ip__badge--' + def.variant : ''));
  if (def.icon) badge.appendChild(iconBox(def.icon, 'bs-ip__badge-icon'));
  const body = el('span', 'bs-ip__badge-body');
  body.appendChild(el('span', 'bs-ip__badge-title', def.title));
  if (def.text) body.appendChild(el('span', 'bs-ip__badge-text', def.text));
  badge.appendChild(body);
  return badge;
}

// --- checklist („Co si připravte") -----------------------------------------
// items = [{ icon, title, desc }]
export function buildCheckCard(title, items) {
  const card = el('section', 'bs-ip__check');
  const head = el('div', 'bs-ip__check-head');
  head.appendChild(iconBox('file', 'bs-ip__check-icon'));
  head.appendChild(el('div', 'bs-ip__check-title', title));
  card.appendChild(head);

  const grid = el('div', 'bs-ip__check-grid');
  grid.style.setProperty('--bs-ip-check-cols', String(items.length));
  items.forEach(function (item) {
    const tile = el('div', 'bs-ip__ctile');
    tile.appendChild(iconBox(item.icon || 'check', 'bs-ip__ctile-icon'));
    tile.appendChild(el('span', 'bs-ip__ctile-title', item.title));
    if (item.desc) tile.appendChild(el('span', 'bs-ip__ctile-desc', item.desc));
    grid.appendChild(tile);
  });
  card.appendChild(grid);
  return card;
}

// --- číslovaná timeline v bočním sloupci ------------------------------------
export function buildTimeline(title, steps) {
  const card = el('div', 'bs-ip__steps');
  card.appendChild(el('div', 'bs-ip__steps-title', title));
  const list = el('div', 'bs-ip__steps-list');
  steps.forEach(function (step, i) {
    const row = el('div', 'bs-ip__step');
    const rail = el('div', 'bs-ip__step-rail');
    rail.appendChild(el('span', 'bs-ip__step-num', String(i + 1)));
    if (i < steps.length - 1) rail.appendChild(el('span', 'bs-ip__step-line'));
    row.appendChild(rail);
    const body = el('div', 'bs-ip__step-body');
    body.appendChild(el('div', 'bs-ip__step-name', step.title));
    if (step.desc) body.appendChild(el('div', 'bs-ip__step-desc', step.desc));
    row.appendChild(body);
    list.appendChild(row);
  });
  card.appendChild(list);
  return card;
}

// --- odkazová karta (reklamační řád, obchodní podmínky) ---------------------
export function buildLinkCard(def) {
  const card = link(def.href, 'bs-ip__linkcard');
  card.appendChild(iconBox(def.icon || 'file', 'bs-ip__linkcard-icon'));
  const body = el('span', 'bs-ip__linkcard-body');
  body.appendChild(el('span', 'bs-ip__linkcard-title', def.title));
  if (def.desc) body.appendChild(el('span', 'bs-ip__linkcard-desc', def.desc));
  card.appendChild(body);
  card.appendChild(icon('arrow-right', 'bs-ip__linkcard-arrow'));
  return card;
}

// --- tmavý panel „Nevíte si rady?" ------------------------------------------
export function buildHelpCard(def) {
  const card = el('div', 'bs-ip__help');
  card.appendChild(fillAvatar(el('span', 'bs-ip__help-icon'), 'infoHelp', 42));
  card.appendChild(el('div', 'bs-ip__help-title', def.title));
  card.appendChild(el('p', 'bs-ip__help-text', def.text));
  const mail = link('mailto:' + def.email, 'bs-ip__help-mail');
  mail.appendChild(icon('mail'));
  mail.appendChild(el('span', null, def.email));
  card.appendChild(mail);
  return card;
}

// --- CTA pás na konci stránky -----------------------------------------------
// def = { icon, title, text, actions: [{href,label,icon,variant}] }; akce
// `chat` / `chatless` / `chatVariant` viz chatActions (supportChat.js)
export function buildCtaBand(def) {
  const band = el('section', 'bs-ip__band');
  band.appendChild(iconBox(def.icon || 'headset', 'bs-ip__band-icon'));
  const body = el('div', 'bs-ip__band-body');
  body.appendChild(el('div', 'bs-ip__band-title', def.title));
  body.appendChild(el('div', 'bs-ip__band-text', def.text));
  band.appendChild(body);
  const actions = el('div', 'bs-ip__band-actions');
  // `tel:` akce bez telefonu vypadne (phoneAction) — pás servisu pak nese jen e-mail
  chatActions(def.actions.map(phoneAction).filter(Boolean)).forEach(function (a) {
    const btn = link(a.href, 'bs-ip__cta' + (a.variant ? ' bs-ip__cta--' + a.variant : ''));
    if (a.icon) btn.appendChild(icon(a.icon));
    btn.appendChild(el('span', null, a.label));
    if (a.chat) asChatTrigger(btn, a.chat);
    actions.appendChild(btn);
  });
  band.appendChild(actions);
  return band;
}

// ---------------------------------------------------------------------------
// Média a otevírací doba (#37) — sdílí /kontakt/ i /prodejna-naradi/
// ---------------------------------------------------------------------------

// Bento galerie z REÁLNÝCH fotek administrace (`.plus-gallery-item > a`).
// Kotvy se PŘESOUVAJÍ, ne kopírují — nativní lightbox (colorbox default JS)
// páruje sadu podle `data-gallery`, kopie by z galerie vypadla.
// def = { visible (kolik dlaždic zůstane vidět), visibleM (kolik na mobilu,
// když se počet liší), title, hint }
export function buildGallery(anchors, def) {
  const opts = def || {};
  const visible = opts.visible || 5;
  const visibleM = opts.visibleM || 0;
  const section = el('section', 'bs-ip__gal-sec');
  if (opts.title) section.appendChild(sectionHead(opts.title, opts.hint, 'bs-ip__gal-head'));

  const grid = el('div', 'bs-ip__gal');
  anchors.forEach(function (a, i) {
    const item = el('div', 'bs-ip__gal-item' + (i === 0 ? ' bs-ip__gal-item--big' : ''));
    if (i >= visible) item.classList.add('bs-ip__gal-item--extra');
    if (visibleM && i >= visibleM && i < visible) item.classList.add('bs-ip__gal-item--extra-m');
    const img = a.querySelector('img');
    if (img) {
      // velká dlaždice ~600 px — `*_small.jpg` náhled by na ní byl rozmazaný,
      // originál visí na `href` kotvy
      if (i === 0 && a.getAttribute('href')) img.setAttribute('src', a.getAttribute('href'));
      img.setAttribute('loading', 'lazy');
      if (!img.getAttribute('alt') || !img.getAttribute('alt').trim()) {
        img.setAttribute('alt', opts.alt || L.galleryAlt);
      }
    }
    item.appendChild(a); // přesun (a tím i zachovaný lightbox)
    grid.appendChild(item);
  });

  // „+N" na poslední viditelné dlaždici — skryté fotky zůstávají v lightboxu.
  // Když má mobil jiný počet dlaždic (`visibleM`), nese vlastní odznak s vlastním
  // číslem a CSS mezi nimi přepíná na breakpointu; jinak by mobil lhal o počtu.
  const rest = anchors.length - visible;
  if (rest > 0 && grid.children[visible - 1]) {
    const cls = 'bs-ip__gal-more' + (visibleM ? ' bs-ip__gal-more--d' : '');
    grid.children[visible - 1].appendChild(el('span', cls, '+' + rest));
  }
  const restM = anchors.length - visibleM;
  if (visibleM && restM > 0 && grid.children[visibleM - 1]) {
    grid.children[visibleM - 1].appendChild(el('span', 'bs-ip__gal-more bs-ip__gal-more--m', '+' + restM));
  }
  section.appendChild(grid);
  return section;
}

// Videobox 16/10 s vlastním play tlačítkem. Nativní `<video controls>` se
// PŘESOUVÁ (je z administrace) — overlay jen spustí přehrávání a zmizí.
// def = { title, caption, hint }
export function buildVideoBox(node, def) {
  const opts = def || {};
  const section = el('section', 'bs-ip__vid-sec');
  if (opts.title) {
    const head = sectionHead(opts.title, opts.hint, 'bs-ip__vid-head');
    head.insertBefore(iconBox('play', 'bs-ip__vid-badge'), head.firstChild);
    section.appendChild(head);
  }

  const box = el('div', 'bs-ip__vid');
  if (node) {
    node.removeAttribute('width');
    node.removeAttribute('height');
    node.setAttribute('preload', 'metadata');
    node.classList.add('bs-ip__vid-el');
    box.appendChild(node);
  }
  if (node && node.tagName === 'VIDEO') {
    const play = el('button', 'bs-ip__vid-play');
    play.type = 'button';
    play.setAttribute('aria-label', opts.playLabel || L.playVideo);
    play.appendChild(icon('play'));
    play.addEventListener('click', function () {
      box.classList.add('is-playing');
      const p = node.play();
      if (p && p.catch) p.catch(function () {});
    });
    node.addEventListener('play', function () {
      box.classList.add('is-playing');
    });
    box.appendChild(play);
  }
  if (opts.caption) box.appendChild(el('span', 'bs-ip__vid-cap', opts.caption));
  section.appendChild(box);
  return section;
}

// Mapa: nativní embed (iframe z administrace) se PŘESOUVÁ do boxu 16/10,
// nad ním pin, adresní karta a pill do map. Mobil dostane CTA pod boxem
// (pill uvnitř by kolidoval s adresní kartou — m09 §8).
// def = { title, address, note, href, label, pinOnly }
export function buildMapBox(frame, def) {
  const opts = def || {};
  const box = el('div', 'bs-ip__map');
  if (frame) {
    frame.classList.add('bs-ip__map-frame');
    frame.setAttribute('loading', 'lazy');
    frame.removeAttribute('width');
    frame.removeAttribute('height');
    box.appendChild(frame);
  }
  box.appendChild(iconBox('pin', 'bs-ip__map-pin'));

  if (opts.address) {
    const card = el('div', 'bs-ip__map-card');
    card.appendChild(el('span', 'bs-ip__map-card-title', opts.title || opts.address));
    if (opts.title) card.appendChild(el('span', 'bs-ip__map-card-text', opts.address));
    if (opts.note) card.appendChild(el('span', 'bs-ip__map-card-note', opts.note));
    box.appendChild(card);
  }
  if (opts.href) {
    const pill = link(opts.href, 'bs-ip__map-pill');
    pill.target = '_blank';
    pill.rel = 'noopener noreferrer';
    pill.appendChild(el('span', null, opts.label || L.openMap));
    pill.appendChild(icon('arrow-right'));
    box.appendChild(pill);
  }
  return box;
}

// Otevírací doba jako řádky `space-between`. rows = [{ day, time, closed }]
export function buildHours(rows, cls) {
  const list = el('ul', 'bs-ip__hours' + (cls ? ' ' + cls : ''));
  rows.forEach(function (row) {
    const li = el('li', 'bs-ip__hours-row' + (row.closed ? ' is-closed' : ''));
    li.appendChild(el('span', 'bs-ip__hours-day', row.day));
    li.appendChild(el('span', 'bs-ip__hours-time', row.time));
    list.appendChild(li);
  });
  return list;
}

// Parsování otevírací doby z obsahu administrace (`.opening-hours-item`
// na /kontakt/, textový zápis „Po–Pá 07:30–17:00 • So 08:30–13:00" jinde).
export function hoursFromList(host) {
  const items = host ? host.querySelectorAll('.opening-hours-item') : [];
  const rows = [];
  for (let i = 0; i < items.length; i += 1) {
    const day = textOf(items[i].querySelector('.opening-hours-day'));
    const closed = items[i].querySelector('.opening-hours-closed');
    const time = closed ? textOf(closed) : textOf(items[i].querySelector('.opening-hours-time'));
    if (day) rows.push({ day: day, time: time.replace(/\s*-\s*/, '–'), closed: !!closed });
  }
  return rows;
}
